import shortid from 'shortid'
import { getRawMessage } from '../../../../../locale'
import { removeAndTrimEmptyLines } from 'helpers/generalHelpers'

const checkShuffleOnField = (defaultValue, optionValue) => {
  // there is difference between false and empty values. if the value is not set return true as default value
  if (!['', undefined, null].includes(defaultValue)) return defaultValue
  if (!['', undefined, null].includes(optionValue)) return optionValue
  return true
}

const aliasValidationRegex = /^[a-zA-Z0-9_]+$/
const fileExtensionREgex = /^(?:[a-zA-Z]+(?:,[a-zA-Z]+)*)?/
/*
  number: min_value:: Int, max_value:: Int,
  money => min_value:: Int, max_value:: Int, currency:: (irr, irt, usd, eur, other),
  short_text => max_length:: Int,
  long_text => none,
  website  => none,
  email  => none,
  phone => phone_type:: (mobile, landline, both),
  checkbox  => none,
  yes_no => none,
  choice => choice_items:: [{title, slug (you can call it value)}],
  dropdown => choice_items:: [{title, slug}],
  multiple_select => choice_items:: [{title, slug}],
  matrix => choice_items:: [{title, slug}], choice_groups:: [{title, slug}] ,
  time => from_time:: time string 00:00:00, to_time:: time string 00:00:00 ,
  date => from_date:: Date, to_date:: Date,
  file => file_type:: (image, document, all), max_size:: Int (KBs),
  product => max_value:: Int, min_value:: Int, images:: [{slug, image}], unit_price:: float
  lookup => field, related_form
  */

export const addGenericFields = (
  type,
  { required, description, slug, position, admin_only, is_random_sortable },
  intl,
  options
) => [
  {
    name: 'slug',
    value: slug || '',
    bindings: 'formzField',
  },
  {
    name: 'description',
    label: getRawMessage(
      'pages.formMaker.section.editFieldSettings.fields.description'
    ),
    value: description || options.description || '',
    bindings: 'formzField',
  },
  {
    name: 'required',
    label: getRawMessage(
      'pages.formMaker.section.editFieldSettings.fields.required'
    ),
    value: required || false,
    bindings: 'formzField',
  },
  /*
        by enabling the admin_only, the field won't be displayed for end-user. but it will remain on form editor
      */
  {
    name: 'admin_only',
    label: getRawMessage(
      'pages.formMaker.section.editFieldSettings.fields.adminOnly'
    ),
    value: admin_only || options.admin_only || false,
    bindings: 'formzField',
  },
  /*
        by disabling the is_random_sortable, if the form is on shuffle mode,
        the field always appears in its default position, and shuffling doesn't affect it.
      */
  {
    name: 'is_random_sortable',
    label: getRawMessage(
      'pages.formMaker.section.editFieldSettings.fields.randomSortable'
    ),
    value: checkShuffleOnField(is_random_sortable, options.is_random_sortable),
    bindings: 'formzField',
  },
  {
    name: 'position',
    value: position,
    bindings: 'formzField',
  },

  /*
        NOTE: next 3 fields are not meant to be submitted
        to the server just for our usage internally
      */
  {
    name: 'field_type',
    value: type,
    bindings: 'formzField',
  },
  {
    name: 'id',
    value: shortid.generate(),
    bindings: 'formzField',
  },
  {
    /*  this is the fake value so that user can interact with the field preview */
    name: 'current_value',
    value: '',
    bindings: 'formzField',
  },
]

const commonSettings = {
  title: ({ title }) => ({
    name: 'title',
    label: getRawMessage(
      'pages.formMaker.section.editFieldSettings.fields.title'
    ),
    value: title || '',
    rules: 'required',
    required: true,
    bindings: 'formzField',
  }),
  alias: ({ alias, options }) => ({
    name: 'alias',
    value: alias || options?.alias,
    label: getRawMessage('form.fields.alias.label'),
    bindings: 'formzField',
    rules: `regex:${aliasValidationRegex}`,
  }),
  unique: ({ unique }) => ({
    name: 'unique',
    label: getRawMessage(
      'pages.formMaker.section.editFieldSettings.fields.unique'
    ),
    value: unique,
    bindings: 'formzField',
  }),
  acceptable_answers: ({ acceptable_answers }) => ({
    name: 'acceptable_answers',
    label: getRawMessage('form.fields.acceptable_answers'),
    value: acceptable_answers,
    input: (v) => (Array.isArray(v) ? v.join('\n') : null),
    output: removeAndTrimEmptyLines,
    bindings: 'formzField',
  }),
  defaultValue: ({ defaultValue, options, ...config }) => ({
    name: 'default',
    value: defaultValue ?? options?.default ?? null,
    label: getRawMessage('form.fields.default.label'),
    // input: (v) => (v ? v : ''),
    // output: (v) => (v ? v : null),
    bindings: 'formzField',
    ...config,
  }),
}

// NOTE: fields in this dictionary dont include all specific field for
// each type but only the ones that matter for form settings section
export const typeSpecificFieldsDictionary = {
  number: (
    {
      min_value,
      max_value,
      title,
      default: defaultValue,
      alias,
      decimal_places,
    },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'min_value_selector',
      label: getRawMessage('form.fields.number.minValue'),
      value: min_value,
      output: (v) => (v ? v : null),
      bindings: 'formzField',
    },
    {
      name: 'max_value_selector',
      label: getRawMessage('form.fields.number.maxValue'),
      value: max_value,
      output: (v) => (v ? v : null),
      bindings: 'formzField',
    },
    {
      name: 'decimal_places',
      label: getRawMessage('form.fields.number.decimal'),
      value: decimal_places ?? options.decimal_places ?? null,
      bindings: 'formzField',
      // output: (v) => v ?? null,
      rules: 'between:0,10',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  money: ({ max_value, min_value, currency, title, alias }, options) => [
    commonSettings.title({ title }),
    {
      name: 'min_value_selector',
      label: getRawMessage('form.fields.money.minValue'),
      value: min_value,
      bindings: 'formzField',
    },
    {
      name: 'max_value_selector',
      label: getRawMessage('form.fields.money.maxValue'),
      value: max_value,
      bindings: 'formzField',
    },
    {
      name: 'currency_selector',
      label: getRawMessage('form.fields.money.currency'),
      value: currency,
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
  ],

  short_text: (
    {
      max_length,
      title,
      json_key,
      acceptable_answers,
      unique,
      default: defaultValue,
      alias,
    },
    options
  ) => [
    commonSettings.title({ title }),
    commonSettings.unique({ unique }),
    {
      name: 'max_length_selector',
      label: getRawMessage('form.fields.text.maxLengthSelector.label'),
      value: max_length,
      bindings: 'formzField',
    },
    {
      name: 'json_key',
      value: json_key ? json_key : options.json_key,
      bindings: 'formzField',
    },
    commonSettings.acceptable_answers({ acceptable_answers }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  long_text: ({ title, default: defaultValue, alias }, options) => [
    commonSettings.title({ title }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  website: ({ title, default: defaultValue, alias }, options) => [
    commonSettings.title({ title }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  email: (
    { title, unique, acceptable_answers, default: defaultValue, alias },
    options
  ) => [
    commonSettings.title({ title }),
    commonSettings.unique({ unique }),
    commonSettings.acceptable_answers({ acceptable_answers }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  phone: (
    {
      phone_type,
      title,
      unique,
      acceptable_answers,
      default: defaultValue,
      alias,
    },
    options
  ) => [
    commonSettings.title({ title }),
    commonSettings.unique({ unique }),
    {
      name: 'phone_type_selector',
      label: getRawMessage('form.fields.phone.phoneTypeSelector.label'),
      value: phone_type,
      bindings: 'formzField',
    },
    commonSettings.acceptable_answers({ acceptable_answers }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  checkbox: ({ title, default: defaultValue, alias }, options) => [
    commonSettings.title({ title }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  /*
  "type" in this field is just here to distinguish it from
  choice field, it's not used as the actual type
  */
  yes_no: ({ title, default: defaultValue, alias }, options) => [
    commonSettings.title({ title }),
    {
      name: 'type',
      value: 'yes_no',
      bindings: 'formzField',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  choice: (
    {
      choice_items,
      title,
      has_other_choice,
      shuffle_choices,
      default: defaultValue,
      alias,
    },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'choice_items_creator',
      label: getRawMessage('form.fields.choice.choiceOptionsCreator.label'),
      value: choice_items || options.choice_items || [],
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'bulk_choices',
      label: getRawMessage('form.fields.bulk_choices.bulk_insert'),
      value: null,
      output: removeAndTrimEmptyLines,
      bindings: 'formzField',
    },
    {
      name: 'shuffle_choices',
      label: getRawMessage('form.fields.choices.shuffle'),
      value: shuffle_choices || options.shuffle_choices || false,
      bindings: 'formzField',
    },
    {
      name: 'has_other_choice',
      label: getRawMessage('form.fields.choices.other.label'),
      value: has_other_choice || options.has_other_choice || false,
      bindings: 'formzField',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  dropdown: (
    { choice_items, title, default: defaultValue, alias },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'choice_items_creator',
      label: getRawMessage('form.fields.dropdown.dropdownOptionsCreator.label'),
      value: choice_items || options.choice_items || [],
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'bulk_choices',
      label: getRawMessage('form.fields.bulk_choices.bulk_insert'),
      value: null,
      output: removeAndTrimEmptyLines,
      bindings: 'formzField',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  multiple_select: (
    {
      title,
      choice_items,
      min_selectable_choices,
      max_selectable_choices,
      has_other_choice,
      shuffle_choices,
      default: defaultValue,
      alias,
    },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'choice_items_creator',
      label: getRawMessage(
        'form.fields.multipleSelect.choiceOptionsCreator.label'
      ),
      value: choice_items || options.choice_items || [],
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'bulk_choices',
      label: getRawMessage('form.fields.bulk_choices.bulk_insert'),
      value: null,
      output: removeAndTrimEmptyLines,
      bindings: 'formzField',
    },
    {
      name: 'shuffle_choices',
      label: getRawMessage('form.fields.choices.shuffle'),
      value: shuffle_choices || options.shuffle_choices || false,
      bindings: 'formzField',
    },
    {
      name: 'min_selectable_choices',
      label: getRawMessage('form.fields.choices.minSelectableChoices'),
      value: min_selectable_choices,
      rules: `integer|min:1`,
      output: (v) => (v ? Number(v) : null),
      bindings: 'formzField',
    },
    {
      name: 'max_selectable_choices',
      label: getRawMessage('form.fields.choices.maxSelectableChoices'),
      value: max_selectable_choices,
      rules: `integer|min:1`,
      output: (v) => (v ? Number(v) : null),
      bindings: 'formzField',
    },
    {
      name: 'has_other_choice',
      label: getRawMessage('form.fields.choices.other.label'),
      value: has_other_choice || options.has_other_choice || false,
      bindings: 'formzField',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  matrix: ({ choice_items, choice_groups, title, alias }, options) => [
    commonSettings.title({ title }),
    {
      name: 'choice_items_creator',
      placeholder: '',
      label: getRawMessage('form.fields.matrix.choiceItemsCreator.label'),
      value: choice_items || options.choice_items || [],
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'choice_groups_creator',
      placeholder: '',
      label: getRawMessage('form.fields.matrix.choiceGroupCreator.label'),
      value: choice_groups || options.choice_groups || [],
      rules: 'required',
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
  ],

  time: (
    { from_time, to_time, title, default: defaultValue, alias },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'from_time_selector',
      label: getRawMessage('form.fields.time.from.title'),
      value: from_time,
      bindings: 'formzField',
    },
    {
      name: 'to_time_selector',
      label: getRawMessage('form.fields.time.to.title'),
      value: to_time,
      bindings: 'formzField',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],

  date: (
    {
      title,
      alias,
      date_format,
      default_date,
      custom_default_date,
      from_date,
      to_date,
      relative_range_start,
      relative_range_end,
    },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'date_format',
      label: getRawMessage('form.fields.date.format'),
      value: date_format || options.date_format,
      bindings: 'formzField',
    },
    {
      name: 'from_date_selector',
      label: getRawMessage('form.fields.date.from.title'),
      value: from_date,
      // https://github.com/foxhound87/mobx-react-form/issues/167#issuecomment-399336697
      input: (value) => (value ? value : null),
      output: (value) => (value ? value : null),
      rules: 'date',
      bindings: 'formzField',
    },
    {
      name: 'to_date_selector',
      label: getRawMessage('form.fields.date.to.title'),
      rules: 'date',
      value: to_date,
      // https://github.com/foxhound87/mobx-react-form/issues/167#issuecomment-399336697
      input: (value) => (value ? value : null),
      output: (value) => (value ? value : null),
      bindings: 'formzField',
    },
    {
      name: 'relative_range_start',
      label: getRawMessage('form.fields.date.relative_range_start'),
      value: relative_range_start,
      bindings: 'formzField',
      alwaysSend: true,
    },
    {
      name: 'relative_range_end',
      label: getRawMessage('form.fields.date.relative_range_end'),
      value: relative_range_end,
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
    {
      name: 'default_date',
      label: getRawMessage('form.fields.date.selected_by_default'),
      value: default_date,
      bindings: 'formzField',
    },
    {
      name: 'custom_default_date',
      label: getRawMessage('form.fields.date.custom_default'),
      value: custom_default_date,
      // https://github.com/foxhound87/mobx-react-form/issues/167#issuecomment-399336697
      input: (value) => (value ? value : null),
      output: (value) => (value ? value : null),
      bindings: 'formzField',
    },
  ],

  file: (
    {
      file_type,
      max_size,
      title,
      alias,
      allow_multiple_files,
      min_files_count,
      max_files_count,
      allowed_extensions,
    },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'file_type_selector',
      label: getRawMessage('form.fields.file.fileTypeSelector.label'),
      value: file_type,
      bindings: 'formzField',
    },
    {
      name: 'max_size_selector',
      label: getRawMessage('form.fields.file.maxSizeSelector.label'),
      value: max_size,
      bindings: 'formzField',
    },
    {
      name: 'allow_multiple_files',
      label: getRawMessage('form.fields.file.multipleUpload.label'),
      value: allow_multiple_files || options?.allow_multiple_files,
      bindings: 'formzField',
    },
    {
      name: 'min_files_count',
      label: getRawMessage('form.fields.file.minCountUpload.label'),
      value: min_files_count || options?.min_files_count || 1,
      bindings: 'formzField',
    },
    {
      name: 'max_files_count',
      label: getRawMessage('form.fields.file.maxCountUpload.label'),
      value: max_files_count || options?.max_files_count,
      bindings: 'formzField',
    },
    {
      name: 'allowed_extensions',
      label: getRawMessage('form.fields.file.extensions.label'),
      value: allowed_extensions || '',
      bindings: 'formzField',
      rules: `regex:${fileExtensionREgex}`,
    },
    commonSettings.alias({ alias, options }),
  ],

  rating: (
    {
      sub_type,
      title,
      min_message,
      max_message,
      thumbnail_type,
      range_start,
      range_end,
      default: defaultValue,
      alias,
    },
    options
  ) => {
    return [
      commonSettings.title({ title }),
      {
        name: 'sub_type',
        value: sub_type || options.sub_type,
        bindings: 'formzField',
      },
      {
        name: 'range_start',
        label: getRawMessage(
          'pages.formMaker.section.editFieldSettings.fields.rangeStart'
        ),
        value: range_start,
        rules: 'integer|min:0|max:10',
        output: (v) => (v ? Number(v) : null),
        bindings: 'formzField',
      },
      {
        name: 'range_end',
        label: getRawMessage(
          sub_type === 'embeded'
            ? 'pages.formMaker.section.editFieldSettings.fields.ratingAmount'
            : 'pages.formMaker.section.editFieldSettings.fields.rangeEnd'
        ),
        value: range_end,
        rules: `integer|min:${sub_type === 'embeded' ? 2 : 0}|max:10`,
        output: (v) => (v ? Number(v) : null),
        bindings: 'formzField',
      },
      {
        name: 'min_message_selector',
        label: getRawMessage(
          'pages.formMaker.section.editFieldSettings.fields.minMessage'
        ),
        value: min_message || options.min_message,
        bindings: 'formzField',
      },
      {
        name: 'max_message_selector',
        label: getRawMessage(
          'pages.formMaker.section.editFieldSettings.fields.maxMessage'
        ),
        value: max_message || options.max_message,
        bindings: 'formzField',
      },
      {
        name: 'thumbnail_type',
        label: getRawMessage(
          'pages.formMaker.section.editFieldSettings.fields.npsType'
        ),
        value: thumbnail_type || options.thumbnail_type,
        bindings: 'formzField',
      },
      commonSettings.defaultValue({ defaultValue, options }),
      commonSettings.alias({ alias, options }),
    ]
  },
  meta: ({ sub_type, title, video_link, alias }, options) => {
    return [
      {
        name: 'title',
        label: getRawMessage(
          'pages.formMaker.section.editFieldSettings.fields.title'
        ),
        value: title || '',
        bindings: 'formzField',
      },
      {
        name: 'sub_type',
        value: sub_type ? sub_type : options.sub_type,
        bindings: 'formzField',
      },
      {
        name: 'video_link',
        label: getRawMessage(
          'pages.formMaker.section.editFieldSettings.fields.videoLink'
        ),
        value: video_link ? video_link : options.video_link,
        bindings: 'formzField',
        rules:
          sub_type === 'video' || options.sub_type === 'video'
            ? 'required'
            : '',
      },
      commonSettings.alias({ alias, options }),
    ]
  },
  phone_verification: (
    { title, unique, acceptable_answers, alias },
    options
  ) => [
    commonSettings.title({ title }),
    commonSettings.unique({ unique }),
    commonSettings.acceptable_answers({ acceptable_answers }),
    commonSettings.alias({ alias, options }),
  ],

  email_verification: (
    { title, unique, acceptable_answers, alias },
    options
  ) => [
    commonSettings.title({ title }),
    commonSettings.unique({ unique }),
    commonSettings.acceptable_answers({ acceptable_answers }),
    commonSettings.alias({ alias, options }),
  ],

  signature: ({ title }) => [commonSettings.title({ title })],
  product: (
    {
      min_value,
      max_value,
      unit_price,
      images,
      title,
      default: defaultValue,
      alias,
      is_subscription,
      subscribed_item_id,
    },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'min_value_selector',
      label: getRawMessage('form.fields.product.minValue'),
      value: min_value || options.min_value || 0,
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'max_value_selector',
      label: getRawMessage('form.fields.product.maxValue'),
      value: max_value || options.max_value,
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'unit_price_selector',
      label: getRawMessage('form.fields.product.unitPrice'),
      value: unit_price || options.unit_price,
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'images',
      label: getRawMessage('form.fields.product.images'),
      value: images || options.images || [],
      bindings: 'formzField',
    },
    {
      name: 'is_subscription',
      label: getRawMessage('form.fields.product.enableStripe'),
      value: is_subscription || false,
      bindings: 'formzField',
    },
    {
      name: 'subscribed_item_id',
      label: getRawMessage('form.fields.product.stripeId'),
      value: subscribed_item_id || '',
      bindings: 'formzField',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],
  barcode: ({ title, acceptable_answers }) => [
    commonSettings.title({ title }),
    commonSettings.acceptable_answers({ acceptable_answers }),
  ],
  location: ({ title, alias }, options) => [
    commonSettings.title({ title }),
    commonSettings.alias({ alias, options }),
  ],
  hidden: ({ title, default: defaultValue, alias }, options) => [
    commonSettings.title({ title }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],
  city: ({ title, default: defaultValue, alias }, options) => [
    commonSettings.title({ title }),
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],
  table: ({ column_groups, choice_groups, title, alias }, options) => [
    commonSettings.title({ title }),
    {
      name: 'column_groups_creator',
      placeholder: '',
      label: getRawMessage('form.fields.matrix.choiceItemsCreator.label'),
      value: column_groups || options.column_groups || [],
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'choice_groups_creator',
      placeholder: '',
      label: getRawMessage('form.fields.matrix.choiceGroupCreator.label'),
      value: choice_groups || options.choice_groups || [],
      rules: 'required',
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
  ],
  lookup: (
    { related_form, field, title, choice_items, add_new_choice, alias },
    options
  ) => [
    commonSettings.title({ title }),
    {
      name: 'related_form',
      label: getRawMessage('form.fields.lookup.form'),
      placeholder: getRawMessage('form.fields.lookup.form.placeholder'),
      value: related_form,
      rules: 'required',
      bindings: 'formzField',
      output: (form) => {
        if (form && typeof form === 'string') return form
        return form && form.slug
      },
    },
    {
      name: 'field',
      label: getRawMessage('form.fields.lookup.field'),
      placeholder: getRawMessage('form.fields.lookup.field.placeholder'),
      value: field || options.field,
      rules: 'required',
      bindings: 'formzField',
    },
    {
      name: 'add_new_choice',
      label: getRawMessage('form.fields.lookup.creatable'),
      placeholder: '',
      value: add_new_choice || options.add_new_choice,
      bindings: 'formzField',
    },
    {
      name: 'choice_items',
      placeholder: '',
      label: getRawMessage('form.fields.lookup.field'),
      value: choice_items || options.choice_items,
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
  ],

  user: ({ user_form, title, choice_items, alias, auto_assign }, options) => [
    commonSettings.title({ title }),
    {
      name: 'user_form',
      label: getRawMessage('form.fields.userForm.form'),
      placeholder: getRawMessage('form.fields.userForm.form.placeholder'),
      value: user_form,
      rules: 'required',
      bindings: 'formzField',
      output: (form) => {
        if (form && typeof form === 'string') return form
        return form?.slug ?? ''
      },
    },
    {
      name: 'choice_items',
      placeholder: '',
      label: getRawMessage('form.fields.userForm.field'),
      value: choice_items || options.choice_items,
      bindings: 'formzField',
    },
    {
      name: 'auto_assign',
      label: getRawMessage(
        'pages.formMaker.section.editFieldSettings.fields.autoAssign'
      ),
      value: auto_assign || options.auto_assign || true,
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
  ],

  regex: ({ title, regex, default: defaultValue, alias }, options) => [
    commonSettings.title({ title }),
    {
      name: 'regex',
      label: getRawMessage(
        'pages.formMaker.section.editFieldSettings.fields.regex'
      ),
      placeholder: getRawMessage(
        'pages.formMaker.section.editFieldSettings.fields.regex.placeholder'
      ),
      value: regex || '',
      rules: 'required',
      required: true,
      bindings: 'formzField',
    },
    commonSettings.defaultValue({ defaultValue, options }),
    commonSettings.alias({ alias, options }),
  ],
  oembed: ({ title, url, alias, config, oembed }, options) => [
    commonSettings.title({ title }),
    {
      name: 'url',
      label: getRawMessage(
        'pages.formMaker.section.editFieldSettings.fields.oEmbed.url'
      ),
      value: url || '',
      rules: 'required',
      required: true,
      bindings: 'formzField',
    },
    {
      name: 'config',
      label: getRawMessage(
        'pages.formMaker.section.editFieldSettings.fields.oEmbed.config.label'
      ),
      value: config || options?.config || {},
      fields: [
        {
          name: 'width',
          label: getRawMessage(
            'pages.formMaker.section.editFieldSettings.fields.oEmbed.config.width'
          ),
          value: config ? config.width : options?.config?.width || '100%',
        },
        {
          name: 'height',
          label: getRawMessage(
            'pages.formMaker.section.editFieldSettings.fields.oEmbed.config.height'
          ),
          value: config ? config.height : options?.config?.height || '300px',
        },
      ],
      bindings: 'formzField',
    },
    {
      name: 'oembed',
      value: oembed || {},
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
  ],
  variable: (
    { title, alias, default: defaultValue, sub_type, decimal_places },
    options
  ) => [
    commonSettings.title({ title }),
    commonSettings.alias({ alias, options }),
    {
      name: 'sub_type',
      value: sub_type || options?.sub_type,
      label: getRawMessage('form.fields.type.label'),
      bindings: 'formzField',
      rules: 'required',
    },
    {
      name: 'decimal_places',
      label: getRawMessage('form.fields.number.decimal'),
      value: decimal_places ?? options.decimal_places ?? null,
      bindings: 'formzField',
      // output: (v) => v ?? null,
      rules: 'between:0,10',
    },
    commonSettings.defaultValue({ defaultValue, options }),
  ],
  success_page: ({ title, alias }, options) => [
    {
      name: 'title',
      label: getRawMessage(
        'pages.formMaker.section.editFieldSettings.fields.title'
      ),
      value: title || '',
      bindings: 'formzField',
    },
    commonSettings.alias({ alias, options }),
  ],
}

export const addSpecificFields = (fieldType, initialValues, intl, options) =>
  typeSpecificFieldsDictionary[fieldType](initialValues, options)
